import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, Lock, Send } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const INTENTS = [["BUY", "Buying a property"], ["RENT", "Renting a property"], ["SELL", "Selling a property"],
  ["LEASE_OUT", "Leasing out a property"], ["INVEST", "Investing"], ["OTHER", "General inquiry"]];
const EMPTY = { full_name: "", email: "", mobile: "", message: "", intent: "BUY", preferred_location: "", consent: false, website: "" };

function SentState({ pro, duplicate, onClose }) {
  return (
    <div className="py-4 text-center" data-testid={duplicate ? "pro-inquiry-duplicate" : "pro-inquiry-success"}>
      <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
      <h3 className="mt-3 font-display text-lg font-bold text-[#0F2C59]">{duplicate ? "Inquiry already sent" : "Inquiry sent"}</h3>
      <p className="mt-1.5 text-sm text-slate-600">
        {duplicate ? `You contacted ${pro.name} recently. They'll reply using the details you shared.`
          : `${pro.name} has received your message and will reply using the contact details you provided.`}
      </p>
      <Button onClick={onClose} data-testid="pro-inquiry-done" className="mt-5 rounded-xl bg-[#0F2C59] hover:bg-[#143163]">Done</Button>
    </div>
  );
}

// Secure inquiry form for a published profile. Never reveals the professional's private contact.
export function ProInquiryDialog({ open, onOpenChange, pro, slug, user, src, cid }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(null);
  useEffect(() => { if (open) { setErr(""); setSent(null); } }, [open]);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!f.consent) { setErr("Please confirm consent to share your details."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/public/professionals/${slug}/inquiries`, { ...f, src: src || null, cid: cid || null });
      setSent({ duplicate: !!data.duplicate }); setF(EMPTY);
    } catch (ex) { setErr(apiError(ex)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pro-inquiry-dialog" className="max-h-[92vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-[#0F2C59]">Send an inquiry to {pro.name}</DialogTitle>
          <DialogDescription>Your message goes privately to {pro.name}. Their personal contact details stay private.</DialogDescription>
        </DialogHeader>
        {sent ? <SentState pro={pro} duplicate={sent.duplicate} onClose={() => onOpenChange(false)} /> : (
          <form onSubmit={submit} className="space-y-3" data-testid="pro-inquiry-form">
            {user ? (
              <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="pro-inquiry-sending-as">Sending as <span className="font-semibold">{user.name}</span> ({user.email})</p>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                <Input data-testid="pro-inquiry-name" required value={f.full_name} onChange={set("full_name")} placeholder="Full name" maxLength={120} />
                <Input data-testid="pro-inquiry-email" required type="email" value={f.email} onChange={set("email")} placeholder="Email" maxLength={200} />
              </div>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              <Input data-testid="pro-inquiry-mobile" value={f.mobile} onChange={set("mobile")} placeholder="Mobile (optional)" maxLength={40} />
              <Select value={f.intent} onValueChange={set("intent")}>
                <SelectTrigger data-testid="pro-inquiry-intent"><SelectValue /></SelectTrigger>
                <SelectContent>{INTENTS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Input data-testid="pro-inquiry-location" value={f.preferred_location} onChange={set("preferred_location")} placeholder="Preferred location (optional)" maxLength={120} />
            <Textarea data-testid="pro-inquiry-message" required minLength={10} maxLength={2000} rows={4} value={f.message} onChange={set("message")} placeholder={`Hi ${pro.name?.split(" ")[0] || ""}, I'm looking for…`} />
            <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={f.website} onChange={set("website")} className="hidden" />
            <label className="flex items-start gap-2 text-xs leading-relaxed text-slate-600">
              <input type="checkbox" data-testid="pro-inquiry-consent" checked={f.consent} onChange={(e) => setF((s) => ({ ...s, consent: e.target.checked }))} className="mt-0.5" />
              <span>I agree to share my name, email{f.mobile ? ", mobile" : ""} and message with {pro.name} so they can respond to this inquiry, as described in the <Link to="/privacy" className="font-semibold text-[#0F2C59] underline" target="_blank">Privacy Policy</Link>.</span>
            </label>
            {err && <p className="text-sm text-rose-600" data-testid="pro-inquiry-error">{err}</p>}
            <Button type="submit" disabled={busy} data-testid="pro-inquiry-submit" className="w-full rounded-xl bg-[#C79A3B] font-semibold text-[#0F2C59] hover:bg-[#b98d33]">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-1.5 h-4 w-4" /> Send Inquiry</>}
            </Button>
            <p className="flex items-center justify-center gap-1 text-[11px] text-slate-400"><Lock className="h-3 w-3" /> Protected against spam. One inquiry per professional every 24 hours.</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ProInquiryDialog;
